import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    IconButton,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Tooltip
} from "@mui/material";
import SignalWifi4BarIcon from "@mui/icons-material/SignalWifi4Bar";
import SignalWifiOffIcon from "@mui/icons-material/SignalWifiOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningIcon from "@mui/icons-material/Warning";
import { API_BASE_URL } from "../config";
import SignalTimeline from "../components/SignalTimeline";
import { fetchActiveInstruments } from "../services/api";

const PREDEFINED_STRATEGIES = [
    "INST_001",
    "INST_002",
    "INST_003",
    "INST_004",
    "INST_005",
    "INST_006",
    "INST_007",
    "INST_008",
    "INST_009",
    "INST_010",
    "INST_011",
    "INST_012",
    "INST_013",
    "INST_014",
    "INST_015",
    "INST_016",
    "INST_017",
    "INST_018",
    "INST_019",
    "INST_020",
    "INST_021",
    "INST_022",
    "INST_023",
    "INST_024",
    "INST_025"
];

const MAX_TICKS = 120; // Number of ticks to persist

const Signals = () => {
    const [signalsData, setSignalsData] = useState({});
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [lastUpdate, setLastUpdate] = useState(null);
    const [tickHistory, setTickHistory] = useState([]);
    const [filteredCombinations, setFilteredCombinations] = useState([]);
    const [signalTypeFilter, setSignalTypeFilter] = useState('all');
    const [strengthFilter, setStrengthFilter] = useState('all');
    const [isLoading, setIsLoading] = useState(false);
    const [availableInstruments, setAvailableInstruments] = useState([]);
    const [instrumentsLoading, setInstrumentsLoading] = useState(true);
    const [instrumentsError, setInstrumentsError] = useState(null);
    const [timelines, setTimelines] = useState(() => {
        // Load timelines from localStorage on component mount
        try {
            const savedTimelines = localStorage.getItem('signals-timelines');
            const savedNextId = localStorage.getItem('signals-next-timeline-id');
            if (savedTimelines) {
                const parsedTimelines = JSON.parse(savedTimelines);
                return parsedTimelines;
            }
        } catch (error) {
            console.error('Error loading timelines from localStorage:', error);
        }
        return [];
    });
    
    const [nextTimelineId, setNextTimelineId] = useState(() => {
        // Load next timeline ID from localStorage on component mount
        try {
            const savedNextId = localStorage.getItem('signals-next-timeline-id');
            if (savedNextId) {
                return parseInt(savedNextId, 10);
            }
        } catch (error) {
            console.error('Error loading next timeline ID from localStorage:', error);
        }
        return 1;
    });

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);

    // Fetch active instruments from API
    useEffect(() => {
        const loadInstruments = async () => {
            try {
                setInstrumentsLoading(true);
                setInstrumentsError(null);
                const response = await fetchActiveInstruments();
                if (response.status === 'success' && response.data?.instruments) {
                    setAvailableInstruments(response.data.instruments);
                } else {
                    throw new Error(response.message || 'Failed to fetch instruments');
                }
            } catch (error) {
                console.error('Error fetching active instruments:', error);
                setInstrumentsError(error.message || 'Failed to load instruments');
                // Fallback to empty array if API fails
                setAvailableInstruments([]);
            } finally {
                setInstrumentsLoading(false);
            }
        };

        loadInstruments();
    }, []);

    // Save timelines to localStorage
    const saveTimelinesToStorage = useCallback((timelinesToSave) => {
        try {
            localStorage.setItem('signals-timelines', JSON.stringify(timelinesToSave));
        } catch (error) {
            console.error('Error saving timelines to localStorage:', error);
        }
    }, []);

    // Save next timeline ID to localStorage
    const saveNextTimelineIdToStorage = useCallback((id) => {
        try {
            localStorage.setItem('signals-next-timeline-id', id.toString());
        } catch (error) {
            console.error('Error saving next timeline ID to localStorage:', error);
        }
    }, []);

    // Clear localStorage (for debugging/reset purposes)
    const clearTimelineStorage = useCallback(() => {
        try {
            localStorage.removeItem('signals-timelines');
            localStorage.removeItem('signals-next-timeline-id');
            console.log('Timeline storage cleared');
        } catch (error) {
            console.error('Error clearing timeline storage:', error);
        }
    }, []);

    // Get unique instruments from all timelines
    const getUniqueInstruments = useCallback(() => {
        const instruments = timelines
            .map(timeline => timeline.instrument)
            .filter(instrument => instrument && instrument !== '');
        return [...new Set(instruments)]; // Remove duplicates
    }, [timelines]);

    // WebSocket connection management
    const connectWebSocket = useCallback(() => {
        const uniqueInstruments = getUniqueInstruments();
        if (uniqueInstruments.length === 0) {
            // console.log('No instruments selected, skipping WebSocket connection');
            return;
        }

        try {
            var wsUrl = API_BASE_URL.replace('https', 'wss') + '/signals';
            // var wsUrl = 'ws://localhost:8888/signals';

            // console.log('Connecting to WebSocket:', wsUrl);

            // Create WebSocket connection without authentication
            wsRef.current = new WebSocket(wsUrl);

            wsRef.current.onopen = () => {
                // console.log('WebSocket connected');
                setConnectionStatus('connected');
                setIsLoading(false);

                // Send subscription message after a small delay to ensure connection is fully established
                setTimeout(() => {
                    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                        const subscribeMessage = {
                            action: "subscribe",
                            instruments: uniqueInstruments,
                            strategies: PREDEFINED_STRATEGIES
                        };

                        try {
                            wsRef.current.send(JSON.stringify(subscribeMessage));
                            // console.log('Subscription message sent:', subscribeMessage);
                        } catch (error) {
                            console.error('Error sending subscription message:', error);
                        }
                    } else {
                        console.error('WebSocket not ready for sending messages, readyState:', wsRef.current?.readyState);
                    }
                }, 100); // Small delay to ensure connection is fully established
            };

            wsRef.current.onmessage = (event) => {
                // console.log('Raw WebSocket message received:', event.data);
                try {
                    const data = JSON.parse(event.data);
                    // console.log('Parsed WebSocket data:', data);
                    // console.log('Data structure check - has signals?', !!data.signals);
                    // console.log('Signals data:', data.signals);
                    handleSignalsUpdate(data);
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                    console.error('Raw message that failed to parse:', event.data);
                }
            };

            wsRef.current.onclose = () => {
                // console.log('WebSocket disconnected');
                setConnectionStatus('disconnected');

                // Attempt to reconnect after 5 seconds
                reconnectTimeoutRef.current = setTimeout(() => {
                    connectWebSocket();
                }, 5000);
            };

            wsRef.current.onerror = (error) => {
                console.error('WebSocket error:', error);
                setConnectionStatus('error');
                setIsLoading(false);
            };

        } catch (error) {
            console.error('Error connecting to WebSocket:', error);
            setConnectionStatus('error');
        }
    }, [getUniqueInstruments]);

    const disconnectWebSocket = useCallback(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            // Send unsubscribe message before closing
            const uniqueInstruments = getUniqueInstruments();
            const unsubscribeMessage = {
                action: "unsubscribe",
                instruments: uniqueInstruments,
                strategies: PREDEFINED_STRATEGIES
            };

            try {
                wsRef.current.send(JSON.stringify(unsubscribeMessage));
                // console.log('Unsubscribe message sent:', unsubscribeMessage);
            } catch (error) {
                console.error('Error sending unsubscribe message:', error);
            }

            wsRef.current.close();
            wsRef.current = null;
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }, [getUniqueInstruments]);

    const handleSignalsUpdate = useCallback((data) => {
        // console.log('handleSignalsUpdate called with:', data);

        const normalizeSignal = (signal) => {
            if (!signal || typeof signal !== 'object') return signal;
            const normalized = { ...signal };
            // Normalize strength naming to existing UI expectations
            if (normalized.strength === 'MODERATE') normalized.strength = 'MEDIUM';
            if (normalized.strength === 'STRONG') normalized.strength = 'STRONG';
            if (normalized.strength === 'WEAK') normalized.strength = 'WEAK';
            return normalized;
        };

        if (data && typeof data === 'object' && data.signals && typeof data.signals === 'object') {
            const signalsByTimestamp = data.signals;

            // Extract numeric unix-millis timestamps present in the payload
            const timestampKeys = Object.keys(signalsByTimestamp)
                .filter((k) => !Number.isNaN(Number(k)));

            if (timestampKeys.length === 0) {
                // console.log('signals present but no numeric timestamp keys found');
                return;
            }

            // Build ticks for each timestamp bucket in the payload
            const incomingTicks = timestampKeys.map((tsStr) => {
                const tsMillis = Number(tsStr);
                const tsDate = new Date(tsMillis);
                const instrumentsAtTs = signalsByTimestamp[tsStr] || {};

                const normalizedSignals = Object.entries(instrumentsAtTs).reduce((acc, [combination, signal]) => {
                    acc[combination] = normalizeSignal(signal);
                    return acc;
                }, {});

                return { timestamp: tsDate, signals: normalizedSignals };
            });

            // Determine the most recent timestamp for UI indicators
            const mostRecentTick = incomingTicks.reduce((latest, tick) =>
                !latest || tick.timestamp > latest.timestamp ? tick : latest
                , null);

            if (mostRecentTick) {
                setSignalsData(mostRecentTick.signals);
                setLastUpdate(mostRecentTick.timestamp);
            }

            // Merge incoming ticks into history by timestamp
            setTickHistory((prev) => {
                const byTime = new Map(prev.map((t) => [t.timestamp.getTime(), t]));

                for (const tick of incomingTicks) {
                    const key = tick.timestamp.getTime();
                    if (byTime.has(key)) {
                        // Merge/overwrite signals at the same timestamp
                        const existing = byTime.get(key);
                        byTime.set(key, {
                            timestamp: existing.timestamp,
                            signals: { ...existing.signals, ...tick.signals }
                        });
                    } else {
                        byTime.set(key, tick);
                    }
                }

                // Sort newest first and enforce MAX_TICKS
                const merged = Array.from(byTime.values())
                    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
                    .slice(0, MAX_TICKS);

                // console.log('Updated tick history, total ticks:', merged.length);
                return merged;
            });
        } else {
            console.warn('Invalid signals payload received; ignoring:', data);
        }
    }, []);

    // Initialize WebSocket connection
    useEffect(() => {
        setIsLoading(true);
        connectWebSocket();

        return () => {
            disconnectWebSocket();
        };
    }, [connectWebSocket, disconnectWebSocket]);

    // Handle initial connection when timelines are loaded from localStorage
    useEffect(() => {
        const uniqueInstruments = getUniqueInstruments();
        if (uniqueInstruments.length > 0 && connectionStatus === 'disconnected') {
            // If we have saved timelines with instruments, connect to WebSocket
            setIsLoading(true);
            connectWebSocket();
        }
    }, []); // Only run once on mount

    // Generate all possible instrument+strategy combinations
    const generateAllCombinations = useCallback(() => {
        const combinations = [];
        const uniqueInstruments = getUniqueInstruments();
        uniqueInstruments.forEach(instrument => {
            PREDEFINED_STRATEGIES.forEach(strategy => {
                combinations.push(`${instrument}:${strategy}`);
            });
        });
        return combinations;
    }, [getUniqueInstruments]);

    // Always show all predefined combinations
    useEffect(() => {
        const allCombinations = generateAllCombinations();
        setFilteredCombinations(allCombinations);
    }, [generateAllCombinations]);

    const getSignalIcon = (type) => {
        switch (type) {
            case 'ENTRY':
                return <TrendingUpIcon color="success" fontSize="small" />;
            case 'EXIT':
                return <TrendingDownIcon color="error" fontSize="small" />;
            default:
                return <WarningIcon color="warning" fontSize="small" />;
        }
    };

    const getStrengthColor = (strength) => {
        switch (strength) {
            case 'STRONG':
                return 'success';
            case 'MEDIUM':
                return 'warning';
            case 'WEAK':
                return 'error';
            default:
                return 'default';
        }
    };

    const getConfidenceColor = (confidence) => {
        const conf = parseFloat(confidence);
        if (conf >= 0.7) return 'success';
        if (conf >= 0.4) return 'warning';
        return 'error';
    };

    const getSignalColor = (signal) => {
        const strength = signal.strength;
        const type = signal.type;

        // Define color intensities based on strength
        const strengthIntensity = {
            'STRONG': 1.0,
            'MEDIUM': 0.6,
            'WEAK': 0.3
        };

        const intensity = strengthIntensity[strength] || 0.3;

        if (type === 'ENTRY') {
            // Green shades for ENTRY
            return `rgba(76, 175, 80, ${intensity})`; // Material Green
        } else if (type === 'EXIT') {
            // Red shades for EXIT
            return `rgba(244, 67, 54, ${intensity})`; // Material Red
        } else {
            // Default gray for unknown types
            return `rgba(158, 158, 158, ${intensity})`; // Material Gray
        }
    };

    const formatTimestamp = (timestamp) => {
        return timestamp.toLocaleTimeString();
    };

    const formatTimestampShort = (timestamp) => {
        // Returns time in HH:mm:ss format, e.g., 11:59:59
        return timestamp.toLocaleTimeString('en-GB', { hour12: false, minute: '2-digit', second: '2-digit' });
    };

    const formatInstrumentDisplay = (instrument) => {
        if (!instrument) return '';
        
        // Parse instrument format: "NIFTY_14_OCT_25000_CALL" -> "14 Oct - NIFTY 25,000 CALL"
        const parts = instrument.split('_');
        if (parts.length >= 5) {
            const day = parts[1];
            const month = parts[2];
            const strikePrice = parts[3];
            const optionType = parts[4];
            const underlying = parts[0];
            
            // Format strike price with commas
            const formattedStrikePrice = parseInt(strikePrice).toLocaleString();
            const monthMap = {
                'JAN': 'Jan', 'FEB': 'Feb', 'MAR': 'Mar', 'APR': 'Apr',
                'MAY': 'May', 'JUN': 'Jun', 'JUL': 'Jul', 'AUG': 'Aug',
                'SEP': 'Sep', 'OCT': 'Oct', 'NOV': 'Nov', 'DEC': 'Dec'
            };
            
            const formattedMonth = monthMap[month] || month;
            
            return `[${day} ${formattedMonth}] ${underlying} ${formattedStrikePrice} ${optionType}`;
        }
        
        return instrument; // Return original if parsing fails
    };

    const getSignalForCombination = (combination, timestamp) => {
        const tick = tickHistory.find(t =>
            t.timestamp.getTime() === timestamp.getTime()
        );
        if (!tick) return null;

        return tick.signals[combination] || null;
    };

    const handleRefresh = () => {
        disconnectWebSocket();
        setIsLoading(true);
        connectWebSocket();
    };

    // Timeline management functions
    const handleAddTimeline = useCallback(() => {
        const newTimeline = {
            id: nextTimelineId,
            instrument: ''
        };
        const updatedTimelines = [...timelines, newTimeline];
        const updatedNextId = nextTimelineId + 1;
        
        setTimelines(updatedTimelines);
        setNextTimelineId(updatedNextId);
        
        // Save to localStorage
        saveTimelinesToStorage(updatedTimelines);
        saveNextTimelineIdToStorage(updatedNextId);
    }, [timelines, nextTimelineId, saveTimelinesToStorage, saveNextTimelineIdToStorage]);

    const handleRemoveTimeline = useCallback((timelineId) => {
        const updatedTimelines = timelines.filter(timeline => timeline.id !== timelineId);
        setTimelines(updatedTimelines);
        
        // Save to localStorage
        saveTimelinesToStorage(updatedTimelines);
    }, [timelines, saveTimelinesToStorage]);

    const handleInstrumentChange = useCallback((timelineId, instrument) => {
        const updatedTimelines = timelines.map(timeline => 
            timeline.id === timelineId 
                ? { ...timeline, instrument }
                : timeline
        );
        setTimelines(updatedTimelines);
        
        // Save to localStorage
        saveTimelinesToStorage(updatedTimelines);
    }, [timelines, saveTimelinesToStorage]);

    // Reconnect WebSocket when timelines change
    useEffect(() => {
        const uniqueInstruments = getUniqueInstruments();
        if (uniqueInstruments.length > 0) {
            disconnectWebSocket();
            setIsLoading(true);
            connectWebSocket();
        } else if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            // If no instruments selected, disconnect
            disconnectWebSocket();
        }
    }, [timelines, connectWebSocket, disconnectWebSocket, getUniqueInstruments]);

    // Generate combinations for each selected instrument
    const getCombinationsForInstrument = (instrument) => {
        return filteredCombinations.filter(combo => combo.split(':')[0] === instrument);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box mb={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" >
                    <Box>
                        {/* <Typography variant="h4" fontWeight="bold" gutterBottom>
                            Live Trading Signals
                        </Typography> */}
                        <Typography variant="subtitle1" color="text.secondary">
                            Real-time signals for multiple instruments
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        {/* <Typography variant="caption" color="text.secondary">
                            {timelines.length} / 10 timelines
                        </Typography> */}
                        <Tooltip title="Add New Instrument">
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddTimeline}
                                disabled={timelines.length >= 10}
                                size="small"
                            >
                                Add Instrument
                            </Button>
                        </Tooltip>
                        <Chip
                            icon={connectionStatus === 'connected' ? <SignalWifi4BarIcon /> : <SignalWifiOffIcon />}
                            label={connectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                            color={connectionStatus === 'connected' ? 'success' : 'error'}
                            variant="outlined"
                        />
                        <IconButton onClick={handleRefresh} disabled={isLoading}>
                            {isLoading ? <CircularProgress size={20} /> : <RefreshIcon />}
                        </IconButton>
                    </Box>
                </Box>

                {/* Connection Status Alert */}
                {connectionStatus === 'error' && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to connect to signals stream. Please check your connection and try again.
                    </Alert>
                )}

                {/* Instruments Loading Error Alert */}
                {instrumentsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        Failed to load instruments: {instrumentsError}. Please refresh the page.
                    </Alert>
                )}

                {/* Filters */}
                {/* <Card elevation={1} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Signal Type</InputLabel>
                                <Select
                                    value={signalTypeFilter}
                                    label="Signal Type"
                                    onChange={(e) => setSignalTypeFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Types</MenuItem>
                                    <MenuItem value="ENTRY">Entry</MenuItem>
                                    <MenuItem value="EXIT">Exit</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Strength</InputLabel>
                                <Select
                                    value={strengthFilter}
                                    label="Strength"
                                    onChange={(e) => setStrengthFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Strengths</MenuItem>
                                    <MenuItem value="STRONG">Strong</MenuItem>
                                    <MenuItem value="MEDIUM">Medium</MenuItem>
                                    <MenuItem value="WEAK">Weak</MenuItem>
                                </Select>
                            </FormControl>

                            {lastUpdate && (
                                <Typography variant="caption" color="text.secondary">
                                    Last update: {formatTimestamp(lastUpdate)}
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card> */}
            </Box>

            {/* Signals Grid - Dynamic Timeline Layout */}
            {timelines.length > 0 ? (
                <Grid container spacing={2}>
                    {timelines.map((timeline) => (
                        <Grid 
                            key={timeline.id} 
                            size={{ xs: 12, md: 6 }} 
                            sx={{ 
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <SignalTimeline
                                timelineId={timeline.id}
                                selectedInstrument={timeline.instrument}
                                availableInstruments={availableInstruments}
                                tickHistory={tickHistory}
                                combinations={timeline.instrument ? getCombinationsForInstrument(timeline.instrument) : []}
                                signalTypeFilter={signalTypeFilter}
                                strengthFilter={strengthFilter}
                                onRemoveTimeline={handleRemoveTimeline}
                                onInstrumentChange={handleInstrumentChange}
                                getSignalForCombination={getSignalForCombination}
                                getSignalColor={getSignalColor}
                                formatTimestampShort={formatTimestampShort}
                                formatInstrumentDisplay={formatInstrumentDisplay}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Card elevation={1}>
                    <CardContent>
                        <Typography variant="h6" color="text.secondary" align="center">
                            No timelines created. Click "Add Instrument" to start monitoring instruments.
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default Signals;
